import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BiTag } from '@euler/app/flows/bi-dashboards/components/ui/BiTag';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetColumn, BiDatasetColumnRole } from '@euler/model/bi/dataset';
import { ChevronUp, Link2, Pencil } from 'lucide-react-native';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import { biFormatSummary } from '../../bi-visualization/number-format/functions/biFormatSummary';
import {
  kBiColumnRoleLabels,
  kBiConformedKeyLabels,
  kBiFieldTypeLabels,
} from '../functions/biUploadLabels';

const kRoleColors: Record<BiDatasetColumnRole, string> = {
  [BiDatasetColumnRole.Dimension]: aiColors.primary,
  [BiDatasetColumnRole.Measure]: aiColors.green,
  [BiDatasetColumnRole.Time]: aiColors.violet,
};

/** A column as understood: label, and its type, role, unit and key. */
export const ColumnRow = memo(
  ({
    column,
    primaryTime,
    editing,
    disabled,
    onToggle,
  }: {
    column: BiDatasetColumn;
    /** The time a date range applies to. */
    primaryTime: boolean;
    editing: boolean;
    disabled: boolean;
    onToggle: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const renamed = column.label.trim() !== column.sourceHeader;
    const Icon = editing ? ChevronUp : Pencil;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${editing ? '收起' : '修改'}「${
          column.label || column.sourceHeader
        }」`}
        accessibilityState={{ expanded: editing, disabled }}
        disabled={disabled}
        onPress={onToggle}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(10),
          backgroundColor: editing
            ? aiColors.muted
            : pressed
            ? aiColors.muted
            : aiColors.card,
        })}
      >
        <View style={{ flex: 1, gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'baseline',
              gap: rpx(6),
            }}
          >
            <Typography.Text
              medium
              size={rpx(13)}
              color={
                column.label.trim() ? aiColors.foreground : aiColors.destructive
              }
              numberOfLines={1}
              style={{ flexShrink: 1 }}
            >
              {column.label.trim() || '（未命名）'}
            </Typography.Text>
            {renamed ? (
              <Typography.Text
                size={rpx(11)}
                color={aiColors.mutedForeground}
                numberOfLines={1}
                style={{ flexShrink: 1 }}
              >
                表头：{column.sourceHeader}
              </Typography.Text>
            ) : null}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
            <BiTag
              text={kBiColumnRoleLabels[column.role]}
              color={kRoleColors[column.role]}
            />
            <BiTag
              text={kBiFieldTypeLabels[column.type]}
              color={aiColors.mutedForeground}
            />
            {column.format && column.role === BiDatasetColumnRole.Measure ? (
              <BiTag
                text={biFormatSummary(column.format)}
                color={aiColors.mutedForeground}
              />
            ) : null}
            {column.conformedKey ? (
              <BiTag
                text={kBiConformedKeyLabels[column.conformedKey]}
                color={aiColors.amber}
                icon={Link2}
              />
            ) : null}
            {primaryTime ? (
              <BiTag text="主时间" color={aiColors.violet} />
            ) : null}
          </View>
        </View>
        {disabled ? null : <Icon size={15} color={aiColors.mutedForeground} />}
      </Pressable>
    );
  },
);
